import { useState, useEffect } from 'react';
import { productos as productosMock } from '../../../mocks/ropa';
import { supabase } from '../../../lib/supabase';

interface Producto {
  id: string;
  nombre: string;
  precio: string;
  tallas: string;
  imagen: string;
}

const PRODUCTOS_FALLBACK: Producto[] = productosMock.map(p => ({
  id: String(p.id),
  nombre: p.nombre,
  precio: p.precio,
  tallas: Array.isArray(p.tallas) ? p.tallas.join(', ') : p.tallas,
  imagen: p.imagen,
}));

export default function RopaSection() {
  const [productos, setProductos] = useState<Producto[]>(PRODUCTOS_FALLBACK);
  const [cargando, setCargando] = useState(true);
  const whatsappNumber = '56994347092';

  useEffect(() => {
    const cargarDesdeSupabase = async () => {
      try {
        const PAGE_SIZE = 8;
        let allData: any[] = [];
        let from = 0;
        let hasMore = true;

        while (hasMore) {
          const { data, error } = await supabase
            .from('productos_ropa')
            .select('id, nombre, precio, tallas, imagen')
            .order('created_at', { ascending: true })
            .range(from, from + PAGE_SIZE - 1);

          if (error) throw error;

          if (data && data.length > 0) {
            allData = [...allData, ...data];
            from += PAGE_SIZE;
            hasMore = data.length === PAGE_SIZE;
          } else {
            hasMore = false;
          }
        }

        if (allData.length > 0) {
          const mapped: Producto[] = allData.map((p) => ({
            id: p.id,
            nombre: p.nombre,
            precio: typeof p.precio === 'number' ? `$${p.precio.toLocaleString('es-CL')}` : String(p.precio),
            tallas: Array.isArray(p.tallas) ? p.tallas.join(', ') : String(p.tallas ?? ''),
            imagen: p.imagen,
          }));
          setProductos(mapped);
        } else {
          setProductos(PRODUCTOS_FALLBACK);
        }
      } catch {
        setProductos(PRODUCTOS_FALLBACK);
      } finally {
        setCargando(false);
      }
    };

    cargarDesdeSupabase();
  }, []);

  const getWhatsAppMessage = (nombreProducto: string) =>
    `Hola, me interesa el producto: ${nombreProducto}, ¿tienen talla/color disponible?`;

  return (
    <section id="ropa" className="py-24 bg-gradient-to-b from-stone-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-stone-800 mb-6 font-serif">
            Catálogo de Ropa
          </h2>
          <p className="text-lg text-stone-600 max-w-3xl mx-auto mb-4 leading-relaxed">
            Ropa finamente escogida en calidad premium, en excelentes condiciones y de retorno con etiquetas.
            Marcas conocidas para mujer y hombre.
          </p>
          <div className="inline-flex flex-col items-center gap-3 bg-amber-50 border-2 border-amber-200 px-6 py-4 rounded-2xl max-w-xl mx-auto">
            <div className="flex items-center gap-2 text-amber-800 text-base font-semibold">
              <i className="ri-information-line text-xl"></i>
              <span>Vitrina Digital — Sin Compra Online</span>
            </div>
            <p className="text-sm text-stone-600 text-center leading-relaxed">
              Prueba en el local y consulta tallas/colores por WhatsApp.<br />
              Stock sujeto a disponibilidad.
            </p>
          </div>
        </div>

        {cargando ? (
          <div className="flex justify-center items-center py-24">
            <div className="w-10 h-10 border-4 border-stone-300 border-t-stone-700 rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-product-shop>
            {productos.length === 0 ? (
              <div className="col-span-full text-center py-16">
                <i className="ri-shopping-bag-line text-6xl text-stone-300 mb-4"></i>
                <p className="text-stone-500 text-lg">No hay productos disponibles.</p>
              </div>
            ) : (
              productos.map((producto) => (
                <div
                  key={producto.id}
                  className="bg-white rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-stone-100 group"
                >
                  <div className="h-80 w-full overflow-hidden bg-stone-50">
                    <img
                      src={producto.imagen}
                      alt={producto.nombre}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="space-y-2">
                      <h3 className="font-bold text-stone-900 text-xl leading-tight">{producto.nombre}</h3>
                      <p className="text-3xl font-bold text-stone-800">{producto.precio}</p>
                    </div>

                    <div className="space-y-3 pt-2 border-t border-stone-100">
                      <div className="flex items-start gap-3 text-sm text-stone-600">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-ruler-line text-base"></i>
                        </div>
                        <span className="leading-relaxed">
                          <strong>{producto.tallas && producto.tallas.includes(',') ? 'Tallas' : 'Talla'}:</strong> {producto.tallas}
                        </span>
                      </div>
                    </div>

                    <a
                      href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(getWhatsAppMessage(producto.nombre))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full bg-green-600 hover:bg-green-700 text-white text-center px-6 py-4 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap text-sm mt-6"
                    >
                      <i className="ri-whatsapp-line mr-2 text-lg"></i>
                      Pedir por WhatsApp
                    </a>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </section>
  );
}
